import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import moment from 'moment';

import { DatePicker, Card, Row, Col, Badge, Tabs, Select, Space, Divider, Button, Spin, Tooltip, Modal, message } from 'antd';
import { EditOutlined, PlusOutlined, CloseOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import * as ToolRequest from '../../../tools/ToolRequest';

import StationDashboardGraphInputChart from './StationDashboardGraphInputChart';
import StationDashboardGraphOutputChart from './StationDashboardGraphOutputChart';
import StationDashboardCustomGraphInputChart from './StationDashboardCustomGraphInputChart';
import StationDashboardGraphCustomGraphEditModal from './StationDashboardGraphCustomGraphEditModal';

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function StationDashboardGraphTab(props) {
  const {
    station,
    zones,
    isAllZones,
  } = props;

  const [chartComponentKey, setChartComponentKey] = useState(Math.random().toString());

  const [chartSelectedRange, setChartSelectedRange] = useState(4);
  const endAtToday = moment().startOf('minute');
  const beginAtToday = moment(endAtToday).subtract(4, 'hour');

  const [chartSelectedDates, setChartSelectedDates] = useState([beginAtToday, endAtToday]);

  const [customGraphEditModal, setCustomGraphEditModal] = useState({
    key: Math.random().toString(),
    visible: false,
  });

  const [stationGraphs, setStationGraphs] = useState(null);

  useEffect(() => {
    if (!isAllZones) {
      return;
    }

    setStationGraphs(null);

    (async () => {
      const stationGraphsRes = await ToolRequest.request('GET', `/v1/station-graph`, {
        stationId: station.id,
        count: 100,
      });
      setStationGraphs(stationGraphsRes.data);
    })();
  }, [isAllZones, chartComponentKey]);

  return (
    <div id="chartContainer">
      <div className="top-nav">
        <Row>
          <Col flex="auto">
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                setCustomGraphEditModal({
                  key: Math.random().toString(),
                  visible: true,
                });
              }}
            >
              Add Custom Graph
            </Button>
          </Col>
          <Col style={{ textAlign: 'right' }}>
            <Space>
              <span>Range:</span>
              {!chartSelectedRange && (
                <RangePicker
                  showTime={true}
                  allowClear={false}
                  value={chartSelectedDates}
                  onChange={(dates) => {
                    setChartSelectedDates(dates);
                    setChartComponentKey(Math.random().toString());
                  }}
                />
              )}
              <Select
                value={chartSelectedRange}
                onChange={(value) => {
                  setChartSelectedRange(value);
                  const now = moment().startOf('minute');
                  if (value) {
                    setChartSelectedDates([moment(now).subtract(value, 'hour'), now]);
                  }
                  setChartComponentKey(Math.random().toString());
                }}
                dropdownMatchSelectWidth={150}
              >
                <Option value={1}>Past 1 hour</Option>
                <Option value={4}>Past 4 hours</Option>
                <Option value={8}>Past 8 hours</Option>
                <Option value={12}>Past 12 hours</Option>
                <Option value={24}>Past 24 hours</Option>
                <Option value={48}>Past 2 days</Option>
                <Option value={168}>Past 7 days</Option>
                <Option value={336}>Past 14 days</Option>
                <Option value={720}>Past 30 days</Option>
                <Option value={1440}>Past 60 days</Option>
                <Option value={2160}>Past 90 days</Option>
                <Option value={4320}>Past 180 days</Option>
                <Option value={8760}>Past 365 days</Option>
                <Option value={null}>Custom</Option>
              </Select>
            </Space>
          </Col>
        </Row>
      </div>

      {customGraphEditModal && (
        <StationDashboardGraphCustomGraphEditModal
          key={customGraphEditModal.key}
          stationId={station.id}
          zones={zones}
          stationGraphId={customGraphEditModal.stationGraphId}
          visible={customGraphEditModal.visible}
          onOk={() => {
            setCustomGraphEditModal({
              ...customGraphEditModal,
              visible: false,
            });
            setChartComponentKey(Math.random().toString());
          }}
          onCancel={() => {
            setCustomGraphEditModal({
              ...customGraphEditModal,
              visible: false,
            });
          }}
        />
      )}

      {isAllZones && (
        stationGraphs ? stationGraphs.map((stationGraph, i) => (
        <Card
          key={stationGraph.id}
          className="card-container-sub"
          title={(
            <>
              <Space size="small" style={{ float: 'right' }}>
                {i > 0 && (
                  <Tooltip title="Move up">
                    <Button
                      type="link"
                      size="small"
                      icon={<ArrowUpOutlined />}
                      onClick={() => {
                        Modal.confirm({
                          title: 'Confirm to move a Custom Graph',
                          content: 'Are you sure to move this Custom Graph up?',
                          onOk: () => {
                            (async () => {
                              const previousOrder = stationGraphs[i - 1].order;
                              const currentOrder = stationGraph.order;
                              await Promise.all([
                                ToolRequest.request('PUT', `/v1/station-graph/${stationGraphs[i - 1].id}`, {
                                  order: currentOrder,
                                }),
                                ToolRequest.request('PUT', `/v1/station-graph/${stationGraph.id}`, {
                                  order: previousOrder,
                                }),
                              ]);

                              message.success('Update Custom Graphs successfully');
                              setChartComponentKey(Math.random().toString());
                            })();
                          },
                        });
                      }}
                    />
                  </Tooltip>
                )}
                {i < stationGraphs.length - 1 && (
                  <Tooltip title="Move down">
                    <Button
                      type="link"
                      size="small"
                      icon={<ArrowDownOutlined />}
                      onClick={() => {
                        Modal.confirm({
                          title: 'Confirm to move a Custom Graph',
                          content: 'Are you sure to move this Custom Graph down?',
                          onOk: () => {
                            (async () => {
                              const nextOrder = stationGraphs[i + 1].order;
                              const currentOrder = stationGraph.order;
                              await Promise.all([
                                ToolRequest.request('PUT', `/v1/station-graph/${stationGraphs[i + 1].id}`, {
                                  order: currentOrder,
                                }),
                                ToolRequest.request('PUT', `/v1/station-graph/${stationGraph.id}`, {
                                  order: nextOrder,
                                }),
                              ]);

                              message.success('Update Custom Graphs successfully');
                              setChartComponentKey(Math.random().toString());
                            })();
                          },
                        });
                      }}
                    />
                  </Tooltip>
                )}
                <Tooltip title="Edit">
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setCustomGraphEditModal({
                        key: Math.random().toString(),
                        visible: true,
                        stationGraphId: stationGraph.id,
                      });
                    }}
                  />
                </Tooltip>
                <Tooltip title="Remove">
                  <Button
                    type="link"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => {
                      Modal.confirm({
                        title: 'Confirm to remove a Custom Graph',
                        content: 'Are you sure to REMOVE this Custom Graph?',
                        onOk: () => {
                          (async () => {
                            await ToolRequest.request('DELETE', `/v1/station-graph/${stationGraph.id}`);
                            message.success('Removed Custom Graphs successfully');
                            setChartComponentKey(Math.random().toString());
                          })();
                        },
                      });
                    }}
                  />
                </Tooltip>
              </Space>

              <span>{stationGraph.name}</span>
            </>
          )}
        >
          <StationDashboardCustomGraphInputChart
            key={`${stationGraph.id}-${chartComponentKey}`}
            zones={zones}
            name={stationGraph.name}
            data={stationGraph.data}
            beginAt={chartSelectedDates[0]}
            endAt={chartSelectedDates[1]}
            selectedRange={chartSelectedRange}
          />
        </Card>
        )) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40 }}>
            <Spin size="small" />
          </div>
        )
      )}

      {zones.map(zone => (
        <Card
          key={zone.id}
          className="card-container"
          style={{marginTop: 16}}
          title={zone.name}
        >
          <Divider>Sensors</Divider>
          <Row gutter={8}>
            {zone.ios.filter(zoneIo => zoneIo.config.inputs).map(zoneIo => {
              let suffix;
              let min = zoneIo.settings.min;
              let max = zoneIo.settings.max;
              let interval;
              let formatter = v => v;
              let labelFormatter = v => v;
              let showMarker = true;
              let color = '#2C5BFF';

              switch (zoneIo.type) {
                case 'AIR_HUMIDITY': {
                  suffix = '%';
                  formatter = value => value === null ? '-' : value.toFixed(1);
                  labelFormatter = value => value.toFixed(0);
                  color = '#32B700';
                  break;
                }
                case 'AIR_TEMPERATURE': {
                  suffix = '°C';
                  formatter = value => value === null ? '-' : value.toFixed(1);
                  labelFormatter = value => value.toFixed(0);
                  color = '#32B700';
                  break;
                }
                case 'CO2_PPM': {
                  suffix = 'ppm';
                  formatter = value => value === null ? '-' : value.toFixed(0);
                  labelFormatter = value => value.toFixed(0);
                  color = '#32B700';
                  break;
                }
                case 'EC_VALUE': {
                  suffix = 'us/cm';
                  formatter = value => value === null ? '-' : value.toFixed(0);
                  labelFormatter = value => value.toFixed(0);
                  break;
                }
                case 'PH_VALUE': {
                  formatter = value => value === null ? '-' : value.toFixed(1);
                  labelFormatter = value => value.toFixed(0);
                  break;
                }
                case 'WATER_LEVEL':
                case 'WATER_LEVEL_DIGITAL': {
                  min = 0;
                  max = 2;
                  interval = 1;
                  showMarker = false;
                  formatter = value => ['Low', 'Normal', 'Full'][value] || '-';
                  labelFormatter = value => ['Low', 'Normal', 'Full'][value];
                  break;
                }
                case 'WATER_TEMPERATURE': {
                  suffix = '°C';
                  formatter = value => value === null ? '-' : value.toFixed(1);
                  labelFormatter = value => value.toFixed(0);
                  break;
                }
                case 'DISSOLVED_OXYGEN': {
                  suffix = 'mg/L';
                  formatter = value => value === null ? '-' : value.toFixed(2);
                  labelFormatter = value => value.toFixed(0);
                  break;
                }
                case 'OXYGEN_SATURATION': {
                  suffix = '%';
                  formatter = value => value === null ? '-' : value.toFixed(1);
                  labelFormatter = value => value.toFixed(0);
                  break;
                }
                case 'OXYGEN_PARTIAL_PRESSURE': {
                  suffix = 'hPa';
                  formatter = value => value === null ? '-' : value.toFixed(0);
                  labelFormatter = value => value.toFixed(0);
                  break;
                }
                case 'CURRENT': {
                  suffix = 'A';
                  formatter = value => value === null ? '-' : value.toFixed(2);
                  labelFormatter = value => value.toFixed(2);
                  break;
                }
                case 'ENERGY': {
                  suffix = 'kWh';
                  formatter = value => value === null ? '-' : value.toFixed(0);
                  labelFormatter = value => value.toFixed(0);
                  break;
                }
                case 'CAMERA': {
                  return;
                }
                case 'WATER_LEAK': {
                  min = 0;
                  max = 1;
                  interval = 1;
                  showMarker = false;
                  formatter = value => ['Flood', 'Clear'][value] || '-';
                  labelFormatter = value => ['Flood', 'Clear'][value];
                  break;
                }
              }

              return (
                <Col key={zoneIo.id} xs={24} sm={12}>
                  <Card>
                    <StationDashboardGraphInputChart
                      style={{height: 200}}
                      key={`${zoneIo.id}-${chartComponentKey}`}
                      zoneId={zone.id}
                      zoneIoId={zoneIo.id}
                      ioName={zoneIo.name}
                      settings={zoneIo.settings}
                      suffix={suffix}
                      min={min}
                      max={max}
                      interval={interval}
                      showMarker={showMarker}
                      color={color}
                      formatter={formatter}
                      labelFormatter={labelFormatter}
                      beginAt={chartSelectedDates[0]}
                      endAt={chartSelectedDates[1]}
                      selectedRange={chartSelectedRange}
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>

          <Divider>Controls</Divider>
          <Row gutter={8} style={{ marginTop: 16 }}>
            {zone.ios.filter(zoneIo => zoneIo.config.outputs).map(zoneIo => {
              let color = '#2C5BFF';

              switch (zoneIo.type) {
                case 'LED': {
                  color = '#F0BA00';
                  break;
                }
                case 'MAIN_WATER_PUMP': {
                  color = '#0045E0';
                  break;
                }
                case 'NUTRITION_PUMP': {
                  color = '#00E00B';
                  break;
                }
              }

              return (
                <Col key={zoneIo.id} xs={24} sm={12}>
                  <Card>
                    <StationDashboardGraphOutputChart
                      style={{height: 150}}
                      key={`${zoneIo.id}-${chartComponentKey}`}
                      zoneId={zone.id}
                      zoneIoId={zoneIo.id}
                      ioName={zoneIo.name}
                      color={color}
                      beginAt={chartSelectedDates[0]}
                      endAt={chartSelectedDates[1]}
                      selectedRange={chartSelectedRange}
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      ))}
    </div>
  );
};